import * as Sentry from '@sentry/react';
import React from 'react';

/**
 * Initialize Sentry for error tracking and monitoring
 * @param {Object} [config={}] - Sentry configuration options
 * @param {string} config.dsn - Sentry Data Source Name (required)
 * @param {string} [config.environment] - Application environment (defaults to import.meta.env.MODE or 'production')
 * @param {number} [config.tracesSampleRate=1.0] - Percentage of transactions to trace (0.0 to 1.0)
 * @param {number} [config.replaysSessionSampleRate=0.1] - Session replay sample rate (0.0 to 1.0)
 * @param {number} [config.replaysOnErrorSampleRate=1.0] - Error session replay sample rate (0.0 to 1.0)
 * @returns {void}
 * @example
 * initializeSentry({
 *   dsn: 'https://your-sentry-dsn@sentry.io/project-id',
 *   environment: 'production',
 *   tracesSampleRate: 0.5,
 * });
 */
export const initializeSentry = ({
  dsn,
  environment = import.meta.env.MODE || 'production',
  tracesSampleRate = 1.0,
  replaysSessionSampleRate = 0.1,
  replaysOnErrorSampleRate = 1.0,
} = {}) => {
  // Only initialize Sentry if DSN is provided and Sentry is enabled
  if (!dsn || import.meta.env.VITE_ENABLE_SENTRY !== 'true') {
    console.log(
      'Sentry initialization skipped:',
      !dsn ? 'No DSN provided' : 'Sentry not enabled'
    );
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment,

      // Performance Monitoring
      integrations: [
        // Enable browser tracing
        Sentry.browserTracingIntegration(),

        // Session replay for debugging
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),

        // React-specific integration for React Router v6
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
        }),
      ],

      // Performance monitoring sample rate
      // Set to 1.0 to capture 100% of transactions in production
      // Lower in production to reduce costs
      tracesSampleRate,

      // Session Replay sample rates
      replaysSessionSampleRate, // Sample rate for normal sessions
      replaysOnErrorSampleRate, // Sample rate for sessions with errors

      /**
       * Filter and enhance events before sending to Sentry
       * @param {Object} event - Sentry event object
       * @param {Object} hint - Additional context about the event
       * @returns {Object|null} Modified event or null to drop the event
       */
      beforeSend(event, hint) {
        // Filter out browser extension errors
        if (event.exception) {
          const error = hint.originalException;
          if (
            error &&
            error.message &&
            (error.message.includes('chrome-extension://') ||
              error.message.includes('moz-extension://'))
          ) {
            return null;
          }
        }

        // Add custom context to all events
        event.contexts = {
          ...event.contexts,
          app: {
            app_name: 'Field Inspector Portal',
            app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
          },
        };

        return event;
      },

      // Ignore certain errors that are not actionable
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'chrome-extension://',
        'moz-extension://',

        // Network errors that are expected
        'Network request failed',
        'NetworkError',
        'Failed to fetch',
        'Load failed',

        // Random plugins/extensions
        'atomicFindClose',
        "Can't find variable: ZiteReader",
        'jigsaw is not defined',
        'ComboSearch is not defined',

        // Facebook errors
        'fb_xd_fragment',

        // ResizeObserver errors (common and harmless)
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',

        // Common non-actionable errors
        'Non-Error promise rejection captured',
        'Non-Error exception captured',
      ],

      // Denylist for certain URLs
      denyUrls: [
        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,

        // Common third-party scripts
        /google-analytics\.com/i,
        /googletagmanager\.com/i,
        /facebook\.net/i,
        /connect\.facebook\.net/i,
      ],
    });

    console.log(
      `Sentry initialized successfully in ${environment} environment`
    );
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
};

/**
 * Set user context for Sentry
 * Call this when a user logs in to associate errors with specific users
 * @param {Object|null} user - User information object or null to clear
 * @param {string} user.id - User ID (required)
 * @param {string} [user.email] - User email address
 * @param {string} [user.username] - Username or display name
 * @param {Object} [user.extra] - Additional user data
 * @returns {void}
 * @example
 * setSentryUser({
 *   id: 'user-123',
 *   email: 'user@example.com',
 *   username: 'John Doe',
 *   extra: { role: 'inspector' }
 * });
 */
export const setSentryUser = (user) => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  try {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
      ...user.extra,
    });
  } catch (error) {
    console.error('Error setting Sentry user:', error);
  }
};

/**
 * Clear Sentry user context
 * Call this when a user logs out
 * @returns {void}
 * @example
 * clearSentryUser();
 */
export const clearSentryUser = () => {
  try {
    Sentry.setUser(null);
  } catch (error) {
    console.error('Error clearing Sentry user:', error);
  }
};

/**
 * Add breadcrumb for debugging
 * Breadcrumbs are a trail of events that happened before an error
 * @param {Object} breadcrumb - Breadcrumb data
 * @param {string} breadcrumb.message - Breadcrumb message (required)
 * @param {string} [breadcrumb.category] - Breadcrumb category (e.g., 'navigation', 'auth', 'api')
 * @param {('fatal'|'error'|'warning'|'info'|'debug')} [breadcrumb.level='info'] - Severity level
 * @param {Object} [breadcrumb.data] - Additional structured data
 * @returns {void}
 * @example
 * addSentryBreadcrumb({
 *   message: 'User clicked checkout button',
 *   category: 'user.action',
 *   level: 'info',
 *   data: { productId: '123', quantity: 2 }
 * });
 */
export const addSentryBreadcrumb = ({
  message,
  category,
  level = 'info',
  data,
}) => {
  try {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data,
      timestamp: Date.now() / 1000,
    });
  } catch (error) {
    console.error('Error adding Sentry breadcrumb:', error);
  }
};

/**
 * Capture exception manually
 * Use this to explicitly report errors to Sentry
 * @param {Error} error - Error object to capture
 * @param {Object} [context={}] - Additional context information
 * @returns {string} Event ID that can be used to reference this event
 * @example
 * try {
 *   // risky operation
 * } catch (error) {
 *   captureSentryException(error, {
 *     tags: { module: 'checkout' },
 *     extra: { orderId: '123' }
 *   });
 * }
 */
export const captureSentryException = (error, context = {}) => {
  try {
    return Sentry.captureException(error, {
      contexts: context,
    });
  } catch (sentryError) {
    console.error('Error capturing exception in Sentry:', sentryError);
    return '';
  }
};

/**
 * Capture message manually
 * Use this to log informational messages or warnings to Sentry
 * @param {string} message - Message to capture
 * @param {('fatal'|'error'|'warning'|'info'|'debug')} [level='info'] - Severity level
 * @returns {string} Event ID that can be used to reference this event
 * @example
 * captureSentryMessage('Payment processing started', 'info');
 */
export const captureSentryMessage = (message, level = 'info') => {
  try {
    return Sentry.captureMessage(message, level);
  } catch (error) {
    console.error('Error capturing message in Sentry:', error);
    return '';
  }
};

/**
 * Set custom context/tags
 * Adds additional context to all future events
 * @param {string} key - Context key
 * @param {*} value - Context value (will be serialized to JSON)
 * @returns {void}
 * @example
 * setSentryContext('shopping_cart', {
 *   itemCount: 3,
 *   totalValue: 99.99
 * });
 */
export const setSentryContext = (key, value) => {
  try {
    Sentry.setContext(key, value);
  } catch (error) {
    console.error('Error setting Sentry context:', error);
  }
};

/**
 * Set custom tag
 * Tags are searchable key-value pairs in Sentry
 * @param {string} key - Tag key
 * @param {string} value - Tag value
 * @returns {void}
 * @example
 * setSentryTag('checkout_flow', 'express');
 */
export const setSentryTag = (key, value) => {
  try {
    Sentry.setTag(key, value);
  } catch (error) {
    console.error('Error setting Sentry tag:', error);
  }
};

export default Sentry;
